import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { StoryCard } from '@org/model';
import { STORY_POSTER_STYLES, StoryViewer, cn, type StoryLang, type StoryWoman } from '@org/ui';
import { SegmentedToggle } from './story-form-fields';
import { useStoryLabels } from './story-labels';

type StoryPreviewProps = {
  woman: StoryWoman;
  cards: StoryCard[];
  /** Story page to show: 0 = opening, card i = i + 1, last = closing. */
  pageIndex: number;
  onPageIndexChange: (index: number) => void;
  className?: string;
};

const noop = () => undefined;

/**
 * The story exactly as a student sees it on her phone, fed from what is
 * currently typed in the editor (saved or not).
 */
export function StoryPreview({ woman, cards, pageIndex, onPageIndexChange, className }: StoryPreviewProps) {
  const { t } = useTranslation('admin');
  const [lang, setLang] = useState<StoryLang>('en');
  const [posterStyleIndex, setPosterStyleIndex] = useState(0);
  const labels = useStoryLabels(lang);

  return (
    <div className={cn('flex min-h-0 flex-col gap-3', className)}>
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-3">
        <SegmentedToggle<StoryLang>
          ariaLabel={t('women.preview.language')}
          value={lang}
          onChange={setLang}
          options={[
            { value: 'en', label: t('women.editor.langEn') },
            { value: 'pt', label: t('women.editor.langPt') },
          ]}
        />
        <div className="flex items-center gap-2" role="group" aria-label={t('women.preview.posterColour')}>
          <span className="text-xs text-muted-foreground">{t('women.preview.posterColour')}</span>
          {STORY_POSTER_STYLES.map((style, index) => (
            <button
              key={index}
              type="button"
              aria-pressed={index === posterStyleIndex}
              aria-label={t('women.preview.posterColourN', { n: index + 1 })}
              title={t('women.preview.posterColourN', { n: index + 1 })}
              onClick={() => setPosterStyleIndex(index)}
              className={cn(
                'h-6 w-6 shrink-0 rounded-full border border-border',
                index === posterStyleIndex && 'ring-2 ring-foreground ring-offset-2',
              )}
              style={{ backgroundColor: style.bg }}
            />
          ))}
        </div>
      </div>

      {/* The viewer scales its 390×844 page to this box. The box keeps the
          phone's proportions and follows the column width, so a wider column
          means a larger phone (the column scrolls when it gets taller than
          the screen). */}
      <div className="relative mx-auto aspect-[390/844] w-full max-w-[420px] shrink-0 overflow-hidden rounded-xl bg-neutral-900 lg:max-w-none">
        <div className="absolute inset-3">
          <StoryViewer
            embedded
            mode="preview"
            woman={woman}
            cards={cards}
            lang={lang}
            labels={labels}
            posterStyleIndex={posterStyleIndex}
            initialIndex={pageIndex}
            onIndexChange={onPageIndexChange}
            onClose={noop}
          />
        </div>
      </div>

      <p className="shrink-0 text-xs text-muted-foreground">{t('women.preview.hint')}</p>
    </div>
  );
}
